import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"

// Shared engine for the dashboard grids (tenant dashboard + group dashboard). Everything
// that changes the layout only works in edit mode (see dashboard_edit_mode_controller):
//   - drag to reorder (SortableJS, the whole card is the handle) with optimistic rollback,
//   - the card toolbar: width S/M/L/XL, height in grid rows, move earlier/later,
//   - the keyboard on a focused card: arrows move it, -/+ change its width,
//   - pointer-capture corner-drag resize with width snapping.
// Each change reports an undo entry (`dashboard-grid:changed`) and a screen-reader message
// (`dashboard-grid:announce`) to the edit-mode controller, retracts the entry again when its
// save fails (`dashboard-grid:failed`), and applies the entries it sends back on undo.
// Layout saves run one at a time (see enqueueLayoutRequest): each server action rewrites the
// whole layout, so two in flight at once would let the slower one undo the faster one. Subclasses supply the width->class maps (the two grids use different
// responsive breakpoints) and how a resize is persisted (single endpoint vs per-cell URL).
export default class DashboardGridBase extends Controller {
  static targets = ["grid", "widget"]
  static values = { url: String, errorMessage: String }

  connect() {
    this.editRoot = this.element.closest("[data-editing]") || this.element
    this.boundModeChange = (event) => this.applyEditing(event.detail.editing)
    this.boundUndo = (event) => this.applyUndo(event.detail)
    this.editRoot.addEventListener("dashboard-edit-mode:change", this.boundModeChange)
    this.editRoot.addEventListener("dashboard-edit-mode:undo", this.boundUndo)
    // A group-grid response rendered while a reorder was still waiting to be saved shows the
    // server's older order; the new grid puts the user's order back (see saveOrder).
    if (this.editRoot.pendingOrder) this.showOrder(this.editRoot.pendingOrder)

    this.initializeSortable()
    // Cells appended or replaced by a turbo-stream need the edit-mode tab stop too.
    this.observer = new MutationObserver(() => this.applyEditing(this.editing))
    if (this.hasGridTarget) this.observer.observe(this.gridTarget, { childList: true })
    this.applyEditing(this.editing)
  }

  disconnect() {
    this.editRoot.removeEventListener("dashboard-edit-mode:change", this.boundModeChange)
    this.editRoot.removeEventListener("dashboard-edit-mode:undo", this.boundUndo)
    this.observer?.disconnect()
    this.sortable?.destroy()
  }

  get editing() {
    return this.editRoot.dataset.editing === "true"
  }

  // -- subclass contract -------------------------------------------------

  // { 1: [..classes], 2: [...], 3: [...], 4: [...] } column-span classes per width.
  get colSpanClasses() {
    throw new Error("subclass must implement colSpanClasses")
  }

  // Flat list of every class the maps may have applied (removed before re-adding).
  get colSpanRemovalList() {
    throw new Error("subclass must implement colSpanRemovalList")
  }

  // { url, body } for persisting a widget resize.
  resizeRequest(_container, _widgetId, _newWidth) {
    throw new Error("subclass must implement resizeRequest")
  }

  // { url, body } for persisting a widget height (grid rows).
  heightRequest(_container, _widgetId, _height) {
    throw new Error("subclass must implement heightRequest")
  }

  // -- edit mode -------------------------------------------------------------

  applyEditing(editing) {
    this.sortable?.option("disabled", !editing)
    this.widgets().forEach((widget) => {
      if (editing) {
        widget.setAttribute("tabindex", "0")
      } else {
        widget.removeAttribute("tabindex")
      }
    })
    this.restorePendingFocus()
  }

  widgets() {
    if (!this.hasGridTarget) return []
    return Array.from(this.gridTarget.querySelectorAll(this.widgetSelector))
  }

  get widgetSelector() {
    return `[data-${this.identifier}-target="widget"]`
  }

  cellFor(widgetId) {
    return this.gridTarget.querySelector(`${this.widgetSelector}[data-widget-id="${widgetId}"]`)
  }

  // -- reorder -------------------------------------------------------------

  initializeSortable() {
    if (!this.hasGridTarget) return

    this.sortable = new Sortable(this.gridTarget, {
      draggable: this.widgetSelector,
      // The toolbar's buttons and the widget's own links stay clickable.
      filter: "button, a, input, select, textarea, label, form",
      preventOnFilter: false,
      disabled: !this.editing,
      animation: 150,
      ghostClass: "dashboard-drag-ghost",
      chosenClass: "dashboard-drag-chosen",
      delay: 200,
      delayOnTouchOnly: true,
      touchStartThreshold: 5,
      onStart: () => {
        this.previousOrder = this.currentOrder()
      },
      onEnd: (event) => {
        if (event.oldIndex === event.newIndex) return

        const undo = this.recordChange({ type: "order", ids: this.previousOrder })
        this.saveOrder(this.currentOrder(), this.previousOrder, undo)
        this.announceMove(event.item)
      },
    })
  }

  currentOrder() {
    return this.widgets().map((widget) => widget.dataset.widgetId)
  }

  // Toolbar alternative to drag-and-drop: one step earlier or later in reading order.
  moveWidget(event) {
    const button = event.currentTarget
    const cell = this.cellFor(button.dataset.widgetId)
    if (cell) this.moveBy(cell, button.dataset.direction === "up" ? -1 : 1)
  }

  moveBy(cell, delta) {
    const sibling = delta < 0 ? cell.previousElementSibling : cell.nextElementSibling
    if (!sibling?.matches(this.widgetSelector)) return

    const hadFocus = cell.contains(document.activeElement)
    this.previousOrder = this.currentOrder()
    if (delta < 0) {
      sibling.before(cell)
    } else {
      sibling.after(cell)
    }
    if (hadFocus) cell.focus()

    const undo = this.recordChange({ type: "order", ids: this.previousOrder })
    this.saveOrder(this.currentOrder(), this.previousOrder, undo)
    this.announceMove(cell)
  }

  // Persists `ids` as the order; on failure puts the cells back into `previousIds`. Until a
  // later layout request is sent, `pendingOrder` holds the order the user sees: on the group
  // dashboard, a resize answered while this save waited re-renders the grid in the server's
  // older order (Turbo renders streams on the next repaint, so there is no point in time to
  // re-apply it from here), and the newly connected grid restores it on connect.
  saveOrder(ids, previousIds, undo) {
    this.editRoot.pendingOrder = ids
    this.enqueueLayoutRequest(() => {
      return fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json",
          "Content-type": "application/json",
        },
        body: JSON.stringify({ ordered_ids: ids }),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Layout change failed")
        })
        .catch(() => {
          if (this.editRoot.pendingOrder === ids) this.editRoot.pendingOrder = null
          this.liveGrid()?.showOrder(previousIds)
          this.layoutChangeFailed(undo)
        })
    })
  }

  // Re-appending cells that are already in place would blur the focused one.
  showOrder(ids) {
    if (this.currentOrder().join() !== ids.join()) this.applyOrder(ids)
  }

  applyOrder(ids) {
    const byId = {}
    this.widgets().forEach((widget) => {
      byId[widget.dataset.widgetId] = widget
    })
    ids.forEach((id) => {
      if (byId[id]) this.gridTarget.appendChild(byId[id])
    })
  }

  // -- width -------------------------------------------------------------------

  // Toolbar width control (S/M/L/XL): optimistic resize with rollback.
  setWidth(event) {
    const button = event.currentTarget
    const cell = this.cellFor(button.dataset.widgetId)
    if (cell) this.changeWidth(cell, parseInt(button.dataset.width, 10))
  }

  // Keyboard -/+: the next smaller/larger width the widget allows.
  stepWidth(cell, delta) {
    const allowed = this.allowedWidths(cell)
    const index = allowed.indexOf(this.widthOf(cell)) + delta
    if (index >= 0 && index < allowed.length) this.changeWidth(cell, allowed[index])
  }

  changeWidth(cell, newWidth) {
    const previousWidth = this.widthOf(cell)
    if (newWidth === previousWidth || !this.allowedWidths(cell).includes(newWidth)) return

    this.updateWidgetWidth(cell, newWidth)
    const undo = this.recordChange({
      type: "width",
      widgetId: cell.dataset.widgetId,
      width: previousWidth,
    })
    this.saveWidgetResize(cell, cell.dataset.widgetId, newWidth, previousWidth, undo)
    this.announceWidth(cell, newWidth)
  }

  allowedWidths(cell) {
    return JSON.parse(cell.dataset.widgetAllowedWidths).sort((a, b) => a - b)
  }

  widthOf(cell) {
    return parseInt(cell.dataset.widgetWidth, 10)
  }

  resizeWidget(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.editing) return

    const button = event.currentTarget
    const widgetId = button.dataset.widgetId
    const container = this.cellFor(widgetId)
    if (!container) return

    const allowedWidths = this.allowedWidths(container)
    if (allowedWidths.length <= 1) return

    const startWidth = this.widthOf(container)
    const hasVirtualWidths = allowedWidths.some((width) => width > 3)
    const startPointerX = event.clientX

    // Calculate grid column layout from the actual DOM
    const gridRect = this.gridTarget.getBoundingClientRect()
    const gap = 16 // gap-4 = 16px
    const colWidth = (gridRect.width - 3 * gap) / 4
    const widgetLeft = container.getBoundingClientRect().left

    // Right-edge positions relative to the widget's left edge for each possible width
    const rightEdge1 = colWidth
    const rightEdge2 = 2 * colWidth + gap
    const rightEdge3 = 3 * colWidth + 2 * gap
    const rightEdge4 = rightEdge3 + colWidth + gap

    // Snap thresholds at midpoints between adjacent right edges
    const snapThreshold1to2 = (rightEdge1 + rightEdge2) / 2
    const snapThreshold2to3 = (rightEdge2 + rightEdge3) / 2
    const snapThreshold3to4 = (rightEdge3 + rightEdge4) / 2

    button.setPointerCapture(event.pointerId)

    const onPointerMove = (e) => {
      let constrained

      if (hasVirtualWidths) {
        const startIndex = allowedWidths.indexOf(startWidth)
        const stepDistance = Math.max(colWidth * 0.45, 80)
        const dragDelta = e.clientX - startPointerX
        const indexDelta = Math.round(dragDelta / stepDistance)
        const targetIndex = Math.max(0, Math.min(startIndex + indexDelta, allowedWidths.length - 1))
        constrained = allowedWidths[targetIndex]
      } else {
        const pointerOffset = e.clientX - widgetLeft

        let targetWidth
        if (pointerOffset < snapThreshold1to2) {
          targetWidth = 1
        } else if (pointerOffset < snapThreshold2to3) {
          targetWidth = 2
        } else if (pointerOffset < snapThreshold3to4) {
          targetWidth = 3
        } else {
          targetWidth = 4
        }

        // Constrain to nearest allowed width
        constrained = allowedWidths.reduce((closest, w) =>
          Math.abs(w - targetWidth) < Math.abs(closest - targetWidth) ? w : closest,
        )
      }

      if (constrained !== this.widthOf(container)) {
        this.updateWidgetWidth(container, constrained)
      }
    }

    const onPointerUp = () => {
      button.removeEventListener("pointermove", onPointerMove)
      button.removeEventListener("pointerup", onPointerUp)

      const newWidth = this.widthOf(container)
      if (newWidth !== startWidth) {
        const undo = this.recordChange({ type: "width", widgetId, width: startWidth })
        this.saveWidgetResize(container, widgetId, newWidth, startWidth, undo)
        this.announceWidth(container, newWidth)
      }
    }

    button.addEventListener("pointermove", onPointerMove)
    button.addEventListener("pointerup", onPointerUp)
  }

  updateWidgetWidth(container, newWidth) {
    container.classList.remove(...this.colSpanRemovalList)
    const classes = this.colSpanClasses[newWidth] || this.colSpanClasses[1]
    container.classList.add(...classes)
    container.dataset.widgetWidth = newWidth
  }

  saveWidgetResize(container, widgetId, newWidth, previousWidth, undo) {
    const { url, body } = this.resizeRequest(container, widgetId, newWidth)
    this.sendStreamRequest(url, body, widgetId).catch(() => {
      this.updateWidgetWidth(container, previousWidth)
      this.layoutChangeFailed(undo)
    })
  }

  // PATCHes a layout change that answers with a turbo-stream (the cell or the whole grid
  // re-rendered), and keeps the keyboard focus on the widget across the replacement.
  sendStreamRequest(url, body, widgetId) {
    return this.enqueueLayoutRequest(() => {
      // Every earlier reorder is saved by now, so this response renders the user's order.
      this.editRoot.pendingOrder = null
      const cell = this.liveGrid()?.cellFor(widgetId)
      if (cell?.contains(document.activeElement)) {
        // Kept on the edit root: the group grid answers with a whole new grid, i.e. a new
        // grid controller, and that one has to pick the focus up.
        this.editRoot.pendingFocus = { widgetId, replaced: cell }
      }

      return fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "text/vnd.turbo-stream.html",
          "Content-type": "application/json",
        },
        body: JSON.stringify(body),
      })
        .then((response) => {
          if (response.ok) return response.text()
          throw new Error("Layout change failed")
        })
        .then((html) => {
          if (html) Turbo.renderStreamMessage(html)
        })
    })
  }

  // Runs `request` (a function returning a promise) once every earlier layout request has
  // settled, and right away when none is pending. The queue lives on the edit root because
  // the group grid's controller is replaced by the very responses it is waiting for.
  enqueueLayoutRequest(request) {
    const root = this.editRoot
    const result = root.layoutRequests ? root.layoutRequests.then(request) : request()
    const settled = result.catch(() => {})
    root.layoutRequests = settled
    settled.then(() => {
      if (root.layoutRequests === settled) root.layoutRequests = null
    })
    return result
  }

  // The grid controller currently on the page: this one, or — once a group-grid response has
  // replaced the grid while a request was queued — the controller of the new grid.
  liveGrid() {
    if (this.element.isConnected) return this

    const element = this.editRoot.querySelector(`[data-controller~="${this.identifier}"]`)
    return (
      element && this.application.getControllerForElementAndIdentifier(element, this.identifier)
    )
  }

  layoutChangeFailed(undo) {
    this.showErrorToast()
    // Dispatched on the edit root: this controller's element may already be detached.
    if (undo)
      this.dispatch("failed", { target: this.editRoot, prefix: "dashboard-grid", detail: { undo } })
  }

  // Runs whenever cells change (MutationObserver) or a grid connects: once the stream has
  // swapped in the new cell, it gets the focus the old one had. Turbo renders streams on
  // the next repaint, so this waits for the DOM instead of guessing a delay.
  restorePendingFocus() {
    const pending = this.editRoot.pendingFocus
    if (!pending) return

    const cell = this.cellFor(pending.widgetId)
    if (!cell || cell === pending.replaced) return

    this.editRoot.pendingFocus = null
    cell.focus()
  }

  // -- height ------------------------------------------------------------------

  // Toolbar height control (grid rows). The server re-renders the cell (tenant) or the grid
  // (group), so there is nothing to apply optimistically.
  setHeight(event) {
    const button = event.currentTarget
    const cell = this.cellFor(button.dataset.widgetId)
    // A blank data-height is "Auto": clear the stored height (tenant grid only).
    const height = button.dataset.height ? parseInt(button.dataset.height, 10) : null
    if (cell) this.changeHeight(cell, height, { record: true })
  }

  // The height the layout stores for the cell — what undo has to put back. The group grid
  // always stores one; the tenant grid overrides this, since its rows can follow the width.
  storedHeightOf(cell) {
    return parseInt(cell.dataset.widgetHeight, 10)
  }

  changeHeight(cell, height, { record }) {
    const previousHeight = this.storedHeightOf(cell)
    if (height === previousHeight) return

    const widgetId = cell.dataset.widgetId
    const undo = record
      ? this.recordChange({ type: "height", widgetId, height: previousHeight })
      : null
    const { url, body } = this.heightRequest(cell, widgetId, height)
    this.sendStreamRequest(url, body, widgetId).catch(() => this.layoutChangeFailed(undo))
  }

  // -- keyboard ----------------------------------------------------------------

  keydown(event) {
    if (!this.editing) return
    const cell = event.target
    if (!cell.matches?.(this.widgetSelector)) return

    const action = {
      ArrowLeft: () => this.moveBy(cell, -1),
      ArrowUp: () => this.moveBy(cell, -1),
      ArrowRight: () => this.moveBy(cell, 1),
      ArrowDown: () => this.moveBy(cell, 1),
      "-": () => this.stepWidth(cell, -1),
      "+": () => this.stepWidth(cell, 1),
      "=": () => this.stepWidth(cell, 1),
      // Only Entf, as the hint says: a stray Backspace must not remove a widget, and the
      // group dashboard has no undo for removals.
      Delete: () => cell.querySelector("[data-dashboard-remove]")?.click(),
    }[event.key]
    if (!action) return

    event.preventDefault()
    action()
  }

  // -- undo --------------------------------------------------------------------

  // Returns the entry, so a failed save can retract exactly this one.
  recordChange(undo) {
    this.dispatch("changed", { prefix: "dashboard-grid", detail: { undo } })
    return undo
  }

  applyUndo(entry) {
    if (entry.type === "order") {
      const previousOrder = this.currentOrder()
      this.applyOrder(entry.ids)
      this.saveOrder(entry.ids, previousOrder, null)
    } else if (entry.type === "width") {
      const cell = this.cellFor(entry.widgetId)
      if (!cell) return

      const previousWidth = this.widthOf(cell)
      this.updateWidgetWidth(cell, entry.width)
      this.saveWidgetResize(cell, entry.widgetId, entry.width, previousWidth)
    } else if (entry.type === "height") {
      const cell = this.cellFor(entry.widgetId)
      if (cell) this.changeHeight(cell, entry.height, { record: false })
    }
    this.announce(this.editRoot.dataset.undoneMessage)
  }

  // -- announcements -------------------------------------------------------------

  announceMove(cell) {
    const position = this.widgets().indexOf(cell) + 1
    this.announce(
      this.format(this.editRoot.dataset.movedMessage, {
        widget: cell.dataset.widgetName,
        position,
        total: this.widgets().length,
      }),
    )
  }

  announceWidth(cell, width) {
    const labels = JSON.parse(this.editRoot.dataset.widthLabels || "{}")
    this.announce(
      this.format(this.editRoot.dataset.resizedMessage, {
        widget: cell.dataset.widgetName,
        width: labels[width] || width,
      }),
    )
  }

  announce(message) {
    if (message) this.dispatch("announce", { prefix: "dashboard-grid", detail: { message } })
  }

  format(template, values) {
    return (template || "").replace(/%\{(\w+)\}/g, (_, key) => values[key] ?? "")
  }

  showErrorToast() {
    if (!this.errorMessageValue) return

    const toast = document.createElement("div")
    toast.className =
      "fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg"
    toast.setAttribute("role", "status")
    toast.textContent = this.errorMessageValue
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }

  get csrfToken() {
    return document.head.querySelector('meta[name="csrf-token"]').getAttribute("content")
  }
}
